import React, { createContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import CommonLayout from '@routes/Common/CommonLayout';
import GnbTabs from '@routes/Common/CommonLayout/GnbTabs';
import {
  CapaQuotationChatRoomSpecificAnnotation,
  CapaQuotationChatRoomAnnotationComment,
  useFetchMyQuotationUserQuery,
  CapaActivityType
  // useFetchMyPartnerQuery
} from '@generated/graphql';
import useUserActionLog, {
  Enum_Action_Page_Enum
} from '@hooks/useUserActionLog';
import { menuListItems } from '@components/Chat/ChatMenu/menuListItems';
import { clsx, colorSet } from '@components/Provider';
import {
  CHAT_MOBILE_HEADER_HEIGHT,
  CHAT_MOBILE_MENU_HEIGHT
} from '@components/Chat/constants';
import { MenuStateEnum } from '@components/Chat/useDrawer';
import ViewerPresenter from '@components/Chat/ChatMenu/Viewer/ViewerPresenter';
import {
  TutorialStep1,
  TutorialStep2,
  TutorialStep2_2,
  TutorialStep3,
  TutorialStep4,
  TutorialStep4_2,
  TutorialStep5
} from '.';
import FakeButton from './FakeButton';
import {
  dummyFiles,
  makeMockComments,
  makeMockAnnotation
} from './mock/dummies';
export interface TutorialStepProps {
  handleNextStep: (nextStep: string) => void;
  mockAnnotation: CapaQuotationChatRoomSpecificAnnotation;
  mockComments: CapaQuotationChatRoomAnnotationComment[];
}

export const TutorialContext = createContext<
  CapaQuotationChatRoomAnnotationComment[] | null
>(null);

const CapaViewTutorial = () => {
  const { onInsertUserActionLogOne } = useUserActionLog();
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const history = useHistory();
  const params = useParams<{ step?: string }>();
  const step = params?.step ?? '1';
  const { root } = useStyles();

  const { data } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-only'
  });
  const myEmail = data?.fetchMyQuotationUser?.data?.email ?? '';
  const myName =
    data?.fetchMyQuotationUser?.data?.managerName ??
    data?.fetchMyQuotationUser?.data?.email ??
    '';

  const mockAnnotation = makeMockAnnotation(myName, myEmail);
  const mockComments = makeMockComments(myName);

  const handleNextStep = (nextStep: string) => {
    history.push(`/capaviewtutorial/${nextStep}`, history.location.state);
  };

  useEffect(() => {
    onInsertUserActionLogOne(
      Enum_Action_Page_Enum.Page_Capa_Viewer_Visited,
      CapaActivityType.PageVisit
    );
  }, [onInsertUserActionLogOne]);

  const props: TutorialStepProps = {
    handleNextStep,
    mockAnnotation,
    mockComments
  };

  const PresenterRoute = () => {
    switch (step) {
      case '1':
        return <TutorialStep1 {...props} />;
      case '2':
        return <TutorialStep2 {...props} />;
      case '2-2': {
        if (!matches) {
          history.replace(`/capaviewtutorial/2`);
        }
        // eslint-disable-next-line
        return <TutorialStep2_2 {...props} />;
      }
      case '3':
        return <TutorialStep3 {...props} />;
      case '4':
        return <TutorialStep4 {...props} />;
      case '4-2': {
        if (!matches) {
          history.replace(`/capaviewtutorial/4`);
        }
        // eslint-disable-next-line
        return <TutorialStep4_2 {...props} />;
      }
      case '5': {
        return <TutorialStep5 {...props} />;
      }
      default:
        return <></>;
    }
  };

  return (
    <CommonLayout gnbTabs={<GnbTabs />}>
      {!matches && (
        <div className={root}>
          <List disablePadding className="menu-list-root">
            {menuListItems.map((menu, index) => (
              <ListItem
                key={index}
                button
                className={clsx('menu-item-root', {
                  selected: menu.menu === MenuStateEnum.Viewer
                })}
              >
                <ListItemIcon className="menu-item-icon">
                  {menu.icon}
                </ListItemIcon>
                <ListItemText className="menu-item-text">
                  {menu.name}
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </div>
      )}

      <TutorialContext.Provider value={mockComments}>
        <ViewerPresenter
          mode={'chat'}
          matches={matches}
          files={dummyFiles}
          tab={'ALL'}
          rightDrawer={false}
          currentItem={{
            fileId: '5e445a44-b9b2-4689-8365-6fface8ea91a',
            fileName: 'test.step'
          }}
          fileCategory={'TUTORIAL'}
          isFirstItem={true}
          isLastItem={false}
          canDownload={false}
          onHandleRightDrawer={() => ({})}
          onChangeTab={() => ({})}
          onChangeIndex={() => ({})}
          onDownload={() => ({})}
        />
      </TutorialContext.Provider>
      <PresenterRoute />
      <FakeButton />
    </CommonLayout>
  );
};

export default CapaViewTutorial;

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    position: 'fixed',
    right: 0,
    backgroundColor: colorSet.gray000,
    zIndex: theme.zIndex?.drawer + 1,
    maxWidth: 70,
    width: '100%',
    height: '100%',
    borderLeft: `1px solid ${colorSet.gray400}`,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      height: `${CHAT_MOBILE_HEADER_HEIGHT + CHAT_MOBILE_MENU_HEIGHT + 1}px`,
      borderLeft: 'none',
      borderBottom: `1px solid ${colorSet.gray400}`
    },
    '& .selected': {
      backgroundColor: colorSet.primary100,
      '& svg': {
        color: colorSet.primary500 + ' !important'
      },
      '& span': {
        color: colorSet.primary500 + ' !important'
      }
    },
    '& .menu-list-root': {
      [theme.breakpoints.down('md')]: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
      }
    },
    '& .menu-item-root': {
      maxWidth: 69,
      maxHeight: 69,
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      '& .menu-item-icon': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '100%',
        marginBottom: 2,
        height: 26,
        '& svg': {
          width: 26,
          height: 26,
          color: colorSet.gray900
        }
      },
      '& .menu-item-text': {
        marginTop: 0,
        marginBottom: 0,
        '& > span': {
          fontSize: 10,
          fontWeight: 400,
          lineHeight: '18px',
          color: colorSet.gray900
        }
      }
    }
  }
}));
